/**
 * Payload → chart props for the Overview tab.
 *
 * Every defensive decision lives here rather than in the components: missing
 * keys, non-finite numbers, unknown category names, and category overflow are
 * all resolved before anything reaches recharts. A chart library handed a NaN
 * renders a blank card with no error; a dashboard that silently blanks is worse
 * than one that shows a zero. Keeping this module free of React and recharts
 * also means the hostile-input cases are testable without a DOM.
 */

import type { DashboardExecutiveData } from '../../../types';
import { formatBucketLabel } from './charts/common';

/** Categories beyond this fold into "Other" — past six, a chart stops being readable. */
export const MAX_CATEGORIES = 6;

export interface CategorySlice {
  name: string;
  value: number;
}

export interface GatewayRow extends CategorySlice {
  count: number;
}

// Type aliases, not interfaces: chart props and the empty-state helper take
// index-signature types, and only aliases get an implicit index signature.
export type FlowPoint = {
  bucket: string;
  label: string;
  usdIn: number;
  payoutsOut: number;
  net: number;
};

export type FeePoint = {
  bucket: string;
  label: string;
  playerDeposits: number;
  withdrawals: number;
  creditPurchases: number;
};

export type MoneyInPoint = {
  bucket: string;
  label: string;
  platformPlayers: number;
  shopPlayers: number;
  network: number;
};

export interface CashSettlementView {
  expected: number;
  received: number;
  uncollected: number;
  /** Share of expected cash actually collected, clamped to 0–100 for the gauge. */
  collectedPct: number;
}

type Executive = DashboardExecutiveData | undefined | null;

/** Anything that is not a finite number becomes 0 — never NaN, never Infinity. */
export function safeNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  // Normalise -0 so it never renders as "-$0.00".
  return n === 0 ? 0 : n;
}

function safeArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

const REVENUE_LABELS: Record<string, string> = {
  hierarchyCash: 'Cash collected from super agents',
  wholesaleOnline: 'Online credit sales to super agents',
  systemFees: 'System fees',
};

const LIABILITY_LABELS: Record<string, string> = {
  creditLiability: 'Player credits',
  pendingWithdrawals: 'Pending withdrawals',
  onlineDebt: 'Online debt owed to tiers',
  shopFundingDebt: 'Shop funding debt',
};

const TIER_LABELS: Record<string, string> = {
  super_agent: 'Super agents',
  agent: 'Agents',
  shop: 'Shops',
};

const GATEWAY_LABELS: Record<string, string> = {
  linkmepay: 'LinkMePay',
  btcpay: 'BTCPay',
  zeroxprocessing: '0xProcessing',
  meld: 'Meld',
  ggusonepay: 'GGUSOnePay',
  unknown: 'Unattributed',
};

/** Fall back to the raw key rather than dropping a slice we cannot name. */
function labelFor(map: Record<string, string>, key: unknown): string {
  const raw = typeof key === 'string' && key.trim() !== '' ? key.trim() : 'unknown';
  return map[raw] || raw;
}

/** Display name for a payment gateway — shared so every tab spells them alike. */
export function gatewayLabel(provider: unknown): string {
  return labelFor(GATEWAY_LABELS, provider);
}

function withLabel<T extends { bucket?: unknown }>(row: T): { bucket: string; label: string } {
  const bucket = typeof row.bucket === 'string' ? row.bucket : '';
  return { bucket, label: formatBucketLabel(bucket) };
}

// --- Time series ------------------------------------------------------------

export type BucketPoint = { bucket: string; label: string } & Record<string, number | string>;

/**
 * Shape any bucketed series the backend returns: label the x-axis and coerce
 * every named measure to a finite number. Every tab's charts go through here,
 * so one guarantee covers all of them.
 */
export function toBucketSeries(rows: unknown, keys: string[]): BucketPoint[] {
  return safeArray<Record<string, unknown>>(rows).map((row) => {
    const point = withLabel(row) as BucketPoint;
    for (const key of keys) point[key] = safeNumber(row[key]);
    return point;
  });
}

export function toFlowSeries(data: Executive): FlowPoint[] {
  return toBucketSeries(data?.charts?.flow, ['usdIn', 'payoutsOut', 'net']) as FlowPoint[];
}

export function toFeeSeries(data: Executive): FeePoint[] {
  return toBucketSeries(data?.charts?.fees, [
    'playerDeposits',
    'withdrawals',
    'creditPurchases',
  ]) as FeePoint[];
}

export function toMoneyInSeries(data: Executive): MoneyInPoint[] {
  return toBucketSeries(data?.charts?.moneyIn, [
    'platformPlayers',
    'shopPlayers',
    'network',
  ]) as MoneyInPoint[];
}

/** Player population series — shared by Overview, Platform-Players and Shop-Players. */
export const PLAYER_SERIES_KEYS = ['newPlayers', 'activePlayers', 'totalPlayers'];

export function toPlayerSeries(charts: unknown): BucketPoint[] {
  return toBucketSeries(
    (charts as { players?: unknown } | undefined)?.players,
    PLAYER_SERIES_KEYS,
  );
}

// --- Compositions -----------------------------------------------------------

export function toRevenueMix(data: Executive): CategorySlice[] {
  return safeArray<Record<string, unknown>>(data?.breakdowns?.revenueMix)
    .map((slice) => ({
      name: labelFor(REVENUE_LABELS, slice.key),
      value: safeNumber(slice.value),
    }))
    // A donut cannot draw a negative slice; drop rather than distort the ring.
    .filter((slice) => slice.value > 0);
}

/**
 * The three components of Exposure, and only those — shop funding debt is money
 * owed *to* the platform, so folding it in would make the bar's total disagree
 * with the Exposure card sitting above it.
 */
export const EXPOSURE_KEYS = ['creditLiability', 'pendingWithdrawals', 'onlineDebt'] as const;

export function toExposureBars(data: Executive): CategorySlice[] {
  return safeArray<Record<string, unknown>>(data?.breakdowns?.liability)
    .filter((item) => EXPOSURE_KEYS.includes(item.key as (typeof EXPOSURE_KEYS)[number]))
    .map((item) => ({
      name: labelFor(LIABILITY_LABELS, item.key),
      value: Math.max(0, safeNumber(item.value)),
    }));
}

/** Shop funding debt — the one liability row that points the other way. */
export function toOwedToPlatform(data: Executive): number {
  const row = safeArray<Record<string, unknown>>(data?.breakdowns?.liability).find(
    (item) => item.key === 'shopFundingDebt',
  );
  return Math.max(0, safeNumber(row?.value));
}

export function toTierSplit(data: Executive): CategorySlice[] {
  return safeArray<Record<string, unknown>>(data?.breakdowns?.creditSalesByTier)
    .map((tier) => ({
      name: labelFor(TIER_LABELS, tier.tier),
      value: Math.max(0, safeNumber(tier.amount)),
    }))
    .filter((tier) => tier.value > 0);
}

/**
 * Gateways ranked by volume, with the long tail folded into a single "Other"
 * row so the bar chart keeps a readable number of rows regardless of how many
 * providers get switched on.
 */
export function toGatewayRanking(data: Executive, limit = MAX_CATEGORIES): GatewayRow[] {
  const rows = safeArray<Record<string, unknown>>(data?.breakdowns?.depositsByGateway)
    .map((g) => ({
      name: labelFor(GATEWAY_LABELS, g.provider),
      value: Math.max(0, safeNumber(g.amount)),
      count: Math.max(0, Math.round(safeNumber(g.count))),
    }))
    .filter((g) => g.value > 0)
    .sort((a, b) => b.value - a.value);

  if (rows.length <= limit) return rows;

  const head = rows.slice(0, limit - 1);
  const tail = rows.slice(limit - 1);
  return [
    ...head,
    {
      name: `Other (${tail.length})`,
      value: Math.round(tail.reduce((sum, g) => sum + g.value, 0) * 100) / 100,
      count: tail.reduce((sum, g) => sum + g.count, 0),
    },
  ];
}

export function toCashSettlement(data: Executive): CashSettlementView {
  const raw = data?.breakdowns?.cashSettlement;
  const expected = Math.max(0, safeNumber(raw?.expected));
  const received = Math.max(0, safeNumber(raw?.received));
  const uncollected = safeNumber(raw?.uncollected);
  const collectedPct =
    expected > 0 ? Math.min(100, Math.max(0, (received / expected) * 100)) : received > 0 ? 100 : 0;
  return {
    expected,
    received,
    // Overpayment is possible; a negative gap is not meaningful to show.
    uncollected: Math.max(0, uncollected),
    collectedPct: Math.round(collectedPct * 10) / 10,
  };
}

// --- Empty-state helpers ----------------------------------------------------

/** True when every point across every named series is zero — the "no activity" case. */
export function isSeriesEmpty<T extends Record<string, unknown>>(
  series: T[],
  keys: Array<keyof T>,
): boolean {
  if (series.length === 0) return true;
  return !series.some((point) => keys.some((key) => safeNumber(point[key]) !== 0));
}

export function isCompositionEmpty(slices: CategorySlice[]): boolean {
  return slices.length === 0 || slices.every((slice) => slice.value === 0);
}
